import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@material-ui/core";
import { Edit as EditIcon, DeleteOutline as DeleteOutlineIcon } from "@material-ui/icons";
import TableRowSkeleton from "../TableRowSkeleton";

const useStyles = makeStyles((theme) => {
  const isDark = theme.mode === "dark";

  return {
    tableContainer: {
      borderRadius: 8,
      backgroundColor: isDark ? "#4a4a4a" : "#ffffff",
      boxShadow: isDark
        ? "0 14px 34px rgba(0, 0, 0, 0.24)"
        : "0 12px 30px rgba(19, 27, 46, 0.06)",
    },
    table: {
      minWidth: 760,
      borderCollapse: "separate",
      borderSpacing: "0 4px",
    },
    headerCell: {
      borderBottom: "none",
      color: isDark ? "#93c5fd" : "#4c5d8d",
      backgroundColor: isDark ? "#4a4a4a" : "#ffffff",
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    row: {
      backgroundColor: isDark ? "#505050" : "#ffffff",
      transition: "background-color 180ms ease",
      "&:nth-of-type(even)": {
        backgroundColor: isDark ? "#4b4b4b" : "#f7f9ff",
      },
      "&:hover": {
        backgroundColor: isDark ? "#575757" : "#eef4ff",
      },
      "& td": {
        borderBottom: "none",
        color: isDark ? "#e5e7eb" : "inherit",
        paddingTop: theme.spacing(1.35),
        paddingBottom: theme.spacing(1.35),
      },
    },
    keywordChip: {
      maxWidth: 220,
      color: isDark ? "#dbeafe" : "#0040a2",
      backgroundColor: isDark ? "#0052cc" : "#dae2ff",
      fontWeight: 800,
      "& .MuiChip-label": {
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },
    conditionChip: {
      color: isDark ? "#e0e7ff" : "#415382",
      backgroundColor: isDark ? "rgba(0, 82, 204, 0.45)" : "#e2e7ff",
      fontWeight: 700,
    },
    responseCell: {
      maxWidth: 560,
    },
    responsePreview: {
      display: "-webkit-box",
      overflow: "hidden",
      color: isDark ? "#e5e7eb" : "#434654",
      lineHeight: 1.45,
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
    },
    emptyCell: {
      borderBottom: "none",
      padding: theme.spacing(6, 2),
    },
    emptyState: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing(1),
      color: isDark ? "#94a3b8" : "#737685",
      textAlign: "center",
    },
    emptyTitle: {
      color: isDark ? "#f8fafc" : "#131b2e",
      fontSize: 16,
      fontWeight: 800,
    },
    emptyText: {
      maxWidth: 420,
      fontSize: 13,
      lineHeight: 1.5,
    },
    actionButton: {
      width: 34,
      height: 34,
      margin: theme.spacing(0, 0.25),
      backgroundColor: isDark ? "#424242" : "#ffffff",
      boxShadow: isDark
        ? "0 6px 16px rgba(0, 0, 0, 0.2)"
        : "0 6px 16px rgba(19, 27, 46, 0.08)",
    },
    editButton: {
      color: isDark ? "#7dd3fc" : "#0052cc",
      "&:hover": {
        backgroundColor: isDark ? "rgba(57, 172, 231, 0.18)" : "#dae2ff",
      },
    },
    deleteButton: {
      color: isDark ? "#fca5a5" : "#ba1a1a",
      "&:hover": {
        backgroundColor: isDark ? "rgba(239, 68, 68, 0.16)" : "#ffdad6",
      },
    },
  };
});

const conditionLabels = {
  equal: "Igual",
  contains: "Contem",
  startsWith: "Comeca com",
  endsWith: "Termina com",
};

function AutoRepliesTable({ records, showLoading, editRecord, deleteRecord }) {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (Array.isArray(records)) {
      setRows(records);
    }
    if (showLoading !== undefined) {
      setLoading(showLoading);
    }
  }, [records, showLoading]);

  const renderRows = () => {
    if (!rows.length) {
      return (
        <TableRow>
          <TableCell colSpan={4} className={classes.emptyCell}>
            <Box className={classes.emptyState}>
              <Box className={classes.emptyTitle}>Nenhuma resposta encontrada</Box>
              <Box className={classes.emptyText}>
                Crie uma resposta automatica ou ajuste a busca para encontrar os atalhos cadastrados.
              </Box>
            </Box>
          </TableCell>
        </TableRow>
      );
    }

    return rows.map((record) => {
      const condition = conditionLabels[record.condition] || record.condition || "Sem condicao";
      const keyword = record.keyword || "Sem palavra";
      const response = record.response || "Sem resposta cadastrada";

      return (
        <TableRow key={record.id} className={classes.row}>
          <TableCell align="center">
            <Chip size="small" label={keyword} className={classes.keywordChip} />
          </TableCell>
          <TableCell align="center">
            <Chip size="small" label={condition} className={classes.conditionChip} />
          </TableCell>
          <TableCell align="left" className={classes.responseCell}>
            <span className={classes.responsePreview} title={response}>
              {response}
            </span>
          </TableCell>
          <TableCell align="center">
            <Tooltip title="Editar resposta">
              <IconButton
                size="small"
                onClick={() => editRecord(record)}
                className={`${classes.actionButton} ${classes.editButton}`}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir resposta">
              <IconButton
                size="small"
                onClick={() => deleteRecord(record)}
                className={`${classes.actionButton} ${classes.deleteButton}`}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <TableContainer className={classes.tableContainer}>
      <Table size="small" className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell align="center" className={classes.headerCell}>Palavra</TableCell>
            <TableCell align="center" className={classes.headerCell}>Condicao</TableCell>
            <TableCell align="left" className={classes.headerCell}>Resposta</TableCell>
            <TableCell align="center" className={classes.headerCell}>Acoes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? <TableRowSkeleton columns={4} /> : renderRows()}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

AutoRepliesTable.propTypes = {
  records: PropTypes.array.isRequired,
  showLoading: PropTypes.bool,
  editRecord: PropTypes.func.isRequired,
  deleteRecord: PropTypes.func.isRequired,
};

export default AutoRepliesTable;
